/**
 * 🌐 Configuration Dynamique Multi-Environnement
 * ================================================
 * 
 * Ce module détecte automatiquement l'environnement (Preview/Production)
 * et retourne les URLs appropriées.
 * 
 * ENVIRONNEMENTS SUPPORTÉS:
 * - Production: coachdia.com
 * - Preview: *.preview.emergentagent.com → même domaine
 * - Local: localhost:3000 → localhost:8001
 */

// Configuration des environnements
const ENVIRONMENTS = {
  production: {
    frontendUrl: 'https://coachdia.com',
    backendUrl: 'https://coachdia.com',
  },
  preview: {
    // Preview utilise le même domaine pour frontend et backend
    // Le domaine exact est détecté dynamiquement
    frontendUrl: null, // sera détecté
    backendUrl: null,  // sera détecté
  },
  local: {
    frontendUrl: 'http://localhost:3000',
    backendUrl: 'http://localhost:8001',
  },
};

/**
 * Détecte l'environnement actuel basé sur le hostname
 */
function detectEnvironment() {
  if (typeof window === 'undefined') {
    return 'local';
  }
  
  const hostname = window.location.hostname;
  
  // Production: coachdia.com ou www.coachdia.com
  if (hostname === 'coachdia.com' || hostname === 'www.coachdia.com') {
    return 'production';
  }
  
  // Preview: *.preview.emergentagent.com
  if (hostname.endsWith('.preview.emergentagent.com')) {
    return 'preview';
  }
  
  // Local: localhost
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'local';
  }
  
  // Fallback: utiliser la variable d'environnement si disponible
  const envBackendUrl = window.location.origin || '';
  if (envBackendUrl.includes('coachdia.com')) {
    return 'production';
  }
  if (envBackendUrl.includes('.preview.emergentagent.com')) {
    return 'preview';
  }
  
  return 'local';
}

/**
 * Retourne l'URL du backend pour l'environnement actuel
 */
export function getBackendUrl() {
  const env = detectEnvironment();
  
  if (env === 'production') {
    return ENVIRONMENTS.production.backendUrl;
  }
  
  if (env === 'preview') {
    // Pour preview, utiliser le même domaine que le frontend
    if (typeof window !== 'undefined') {
      return `https://${window.location.hostname}`;
    }
    // Fallback sur la variable d'environnement
    return window.location.origin || 'http://localhost:8001';
  }
  
  // Local: toujours utiliser localhost:8001 pour le backend
  return ENVIRONMENTS.local.backendUrl;
}

/**
 * Retourne l'URL de l'API (backend + /api)
 */
export function getApiUrl() {
  return `${getBackendUrl()}/api`;
}

/**
 * Retourne l'environnement actuel détecté
 */
export function getCurrentEnvironment() {
  return detectEnvironment();
}

/**
 * Retourne l'URL du frontend pour l'environnement actuel
 */
export function getFrontendUrl() {
  const env = detectEnvironment();
  
  if (env === 'production') {
    return ENVIRONMENTS.production.frontendUrl;
  }
  
  if (env === 'preview' && typeof window !== 'undefined') {
    return `https://${window.location.hostname}`;
  }
  
  return typeof window !== 'undefined' 
    ? window.location.origin 
    : ENVIRONMENTS.local.frontendUrl;
}

/**
 * Log la configuration actuelle (pour debug)
 */
export function logConfig() {
  console.log('🌐 Environment Config:', {
    environment: detectEnvironment(),
    backendUrl: getBackendUrl(),
    apiUrl: getApiUrl(),
    frontendUrl: getFrontendUrl(),
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
  });
}

// Export par défaut
const config = {
  getBackendUrl,
  getApiUrl,
  getCurrentEnvironment,
  getFrontendUrl,
  logConfig,
};

export default config;
